import React, { useState, useEffect } from "react";
import { Box, IconButton, Tooltip, Typography, useTheme as useMuiTheme } from "@mui/material";
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from "../helpers/constants";
import { getFetchErrorMessage } from "../helpers/errorUtils";
import ApprovalPanel from "./ApprovalPanel";
import { useTheme } from "../contexts/ThemeContext";

// Approval interface matching the structure used in ThreadDetailPage
export interface Approval {
  id: string;
  approval_details: {
    status: string;
    approver: {
      id: string;
      name: string;
      email_id: string;
      avatar_url?: string | null;
      username: string;
    };
    requested_by: {
      id: string;
      name: string;
      email_id: string;
      avatar_url?: string | null;
      username: string;
    };
    title: string;
    description: string;
    approval_notes?: string;
    rejection_reason?: string;
    comment?: string;
    created_at: string;
    updated_at: string;
    approved_at?: string;
    rejected_at?: string;
    approval_metadata: any;
  };
  approval_history?: Array<{
    action: string;
    details: string;
    timestamp: string;
    user: {
      id: string;
      name: string;
      email_id: string;
      avatar_url?: string | null;
      username: string;
    };
    metadata: any;
  }>;
  context?: any;
}

interface ApprovalsResponse {
  approvals: Approval[];
  total?: number;
  page?: number;
  size?: number;
}

export interface ApprovalPanelButtonProps {
  threadId?: string;
  channelId?: string;
  selectedMessageId?: string | null;
  messages: any[]; // Messages array to check if approvals can be requested
  onApprovalUpdate?: () => void; // Callback when approvals are updated
  open?: boolean; // External control for panel open state
  onOpenChange?: (open: boolean) => void; // Callback when panel open state changes
}

const ApprovalPanelButton: React.FC<ApprovalPanelButtonProps> = ({
  threadId,
  channelId,
  selectedMessageId,
  messages,
  onApprovalUpdate,
  open,
  onOpenChange,
}) => {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();

  // State for approvals
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [approvalsLoading, setApprovalsLoading] = useState<boolean>(false);
  const [approvalsError, setApprovalsError] = useState<string | null>(null);

  // State for approval panel (use external control if provided, otherwise internal state)
  const [isApprovalPanelOpen, setIsApprovalPanelOpen] = useState(false);
  
  // Sync internal state with external open prop
  useEffect(() => {
    if (open !== undefined) {
      setIsApprovalPanelOpen(open);
    }
  }, [open]);

  // Fetch approvals for a specific thread
  const fetchApprovals = async (threadId: string) => {
    if (!threadId) return;

    try {
      setApprovalsLoading(true);
      setApprovalsError(null);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.APPROVALS.LIST}?thread_id=${threadId}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApprovalsResponse = await response.json();
      setApprovals(data.approvals || []);
    } catch (error) {
      setApprovalsError(
        getFetchErrorMessage(error, "Failed to fetch approvals")
      );
    } finally {
      setApprovalsLoading(false);
    }
  };

  // Fetch approvals when threadId changes
  useEffect(() => {
    if (threadId) {
      fetchApprovals(threadId);
    } else {
      setApprovals([]);
    }
  }, [threadId]);

  // Handle approval update completion
  const handleApprovalUpdated = () => {
    if (threadId) {
      fetchApprovals(threadId);
    }
    if (onApprovalUpdate) {
      onApprovalUpdate();
    }
  };

  // Handle opening approval panel
  const handleOpenApprovalPanel = () => {
    setIsApprovalPanelOpen(true);
    // Notify parent component of state change
    if (onOpenChange) {
      onOpenChange(true);
    }
  };

  // Handle closing approval panel
  const handleCloseApprovalPanel = () => {
    setIsApprovalPanelOpen(false);
    // Notify parent component of state change
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  return (
    <>
      <Tooltip
        title={
          messages.length === 0
            ? "No messages in thread"
            : approvals.length > 0
            ? approvals[0]?.approval_details?.approver?.name ||
              "Unknown Approver"
            : "Request Approval"
        }
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "white",
              color: "black",
              border: "1px solid #ccc",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            },
          },
        }}
      >
        {/* Wrapper span so Tooltip can listen to events when IconButton is disabled (MUI requirement) */}
        <Box component="span" sx={{ display: "inline-flex" }}>
          <IconButton
            data-testid="approval-request-button"
            size="small"
            disabled={messages.length === 0}
          onClick={handleOpenApprovalPanel}
          sx={{
            color:
              messages.length === 0
                ? muiTheme.palette.text.disabled
                : muiTheme.palette.text.secondary,
            "&:hover": {
              backgroundColor:
                messages.length === 0
                  ? "transparent"
                  : muiTheme.palette.action.hover,
              color:
                messages.length === 0
                  ? muiTheme.palette.text.disabled
                  : muiTheme.palette.text.secondary,
            },
            "&.Mui-disabled": {
              color: muiTheme.palette.text.disabled,
            },
          }}
        >
          {/* Approval Status Icon - Shows status of first approval if available */}
          {approvals.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor:
                  approvals[0]?.approval_details?.status === "pending"
                    ? isDarkMode
                      ? muiTheme.palette.warning.dark
                      : "#fff3e0"
                    : approvals[0]?.approval_details?.status === "approved"
                    ? isDarkMode
                      ? muiTheme.palette.success.dark
                      : "#e8f5e8"
                    : approvals[0]?.approval_details?.status === "rejected"
                    ? isDarkMode
                      ? muiTheme.palette.error.dark
                      : "#ffebee"
                    : isDarkMode
                    ? muiTheme.palette.warning.dark
                    : "#fff3e0",
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
                minWidth: "fit-content",
                border: `1px solid ${
                  approvals[0]?.approval_details?.status === "pending"
                    ? isDarkMode
                      ? muiTheme.palette.warning.main
                      : "#ffcc80"
                    : approvals[0]?.approval_details?.status === "approved"
                    ? isDarkMode
                      ? muiTheme.palette.success.main
                      : "#c8e6c9"
                    : approvals[0]?.approval_details?.status === "rejected"
                    ? isDarkMode
                      ? muiTheme.palette.error.main
                      : "#ffcdd2"
                    : isDarkMode
                    ? muiTheme.palette.warning.main
                    : "#ffcc80"
                }`,
                cursor: "pointer",
              }}
            >
              {/* Approver initial */}
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor:
                    approvals[0]?.approval_details?.status === "pending"
                      ? "#ff9800"
                      : approvals[0]?.approval_details?.status === "approved"
                      ? "#2e7d32"
                      : approvals[0]?.approval_details?.status === "rejected"
                      ? "#d32f2f"
                      : "#ff9800",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "10px",
                  fontWeight: "bold",
                  flexShrink: 0,
                }}
              >
                {approvals[0]?.approval_details?.approver?.name
                  ?.charAt(0)
                  ?.toUpperCase() || "A"}
              </Box>

              {/* Status text */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: "10px",
                  fontWeight: "500",
                  color:
                    approvals[0]?.approval_details?.status === "pending"
                      ? isDarkMode
                        ? muiTheme.palette.warning.light
                        : "#ff9800"
                      : approvals[0]?.approval_details?.status === "approved"
                      ? isDarkMode
                        ? muiTheme.palette.success.light
                        : "#2e7d32"
                      : approvals[0]?.approval_details?.status === "rejected"
                      ? isDarkMode
                        ? muiTheme.palette.error.light
                        : "#d32f2f"
                      : isDarkMode
                      ? muiTheme.palette.warning.light
                      : "#ff9800",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  whiteSpace: "nowrap",
                }}
              >
                {approvals[0]?.approval_details?.status || "Approval"}
              </Typography>

              {/* Approval Icon - Custom checkmark */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color:
                    approvals[0]?.approval_details?.status === "pending"
                      ? isDarkMode
                        ? muiTheme.palette.warning.light
                        : "#ff9800"
                      : approvals[0]?.approval_details?.status === "approved"
                      ? isDarkMode
                        ? muiTheme.palette.success.light
                        : "#2e7d32"
                      : approvals[0]?.approval_details?.status === "rejected"
                      ? isDarkMode
                        ? muiTheme.palette.error.light
                        : "#d32f2f"
                      : isDarkMode
                      ? muiTheme.palette.warning.light
                      : "#ff9800",
                  ml: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: "12px",
                    height: "6px",
                    borderLeft: `3px solid ${
                      approvals[0]?.approval_details?.status === "pending"
                        ? isDarkMode
                          ? muiTheme.palette.warning.light
                          : "#ff9800"
                        : approvals[0]?.approval_details?.status === "approved"
                        ? isDarkMode
                          ? muiTheme.palette.success.light
                          : "#2e7d32"
                        : approvals[0]?.approval_details?.status === "rejected"
                        ? isDarkMode
                          ? muiTheme.palette.error.light
                          : "#d32f2f"
                        : isDarkMode
                        ? muiTheme.palette.warning.light
                        : "#ff9800"
                    }`,
                    borderBottom: `3px solid ${
                      approvals[0]?.approval_details?.status === "pending"
                        ? isDarkMode
                          ? muiTheme.palette.warning.light
                          : "#ff9800"
                        : approvals[0]?.approval_details?.status === "approved"
                        ? isDarkMode
                          ? muiTheme.palette.success.light
                          : "#2e7d32"
                        : approvals[0]?.approval_details?.status === "rejected"
                        ? isDarkMode
                          ? muiTheme.palette.error.light
                          : "#d32f2f"
                        : isDarkMode
                        ? muiTheme.palette.warning.light
                        : "#ff9800"
                    }`,
                    transform: "rotate(-45deg) translateY(-0.5px)",
                    borderRadius: "1px",
                  }}
                />
              </Box>
            </Box>
          ) : (
            /* Fallback to custom checkmark icon with text when no approvals */
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: isDarkMode
                  ? muiTheme.palette.grey[800]
                  : "#f5f5f5",
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
                minWidth: "fit-content",
                border: `1px solid ${
                  isDarkMode
                    ? muiTheme.palette.grey[700]
                    : "#e0e0e0"
                }`,
                cursor: "pointer",
              }}
            >
              {/* Default approver initial placeholder */}
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: "#666",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: "bold",
                    color: "white",
                    lineHeight: 1,
                  }}
                >
                  A
                </Typography>
              </Box>

              {/* Approval Status Text */}
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: isDarkMode
                    ? muiTheme.palette.text.secondary
                    : "#666",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  whiteSpace: "nowrap",
                }}
              >
                Approval
              </Typography>

              {/* Custom checkmark icon */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isDarkMode
                    ? muiTheme.palette.text.secondary
                    : "#666",
                  ml: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: "12px",
                    height: "6px",
                    borderLeft: `3px solid ${
                      isDarkMode
                        ? muiTheme.palette.text.secondary
                        : "#666"
                    }`,
                    borderBottom: `3px solid ${
                      isDarkMode
                        ? muiTheme.palette.text.secondary
                        : "#666"
                    }`,
                    transform: "rotate(-45deg) translateY(-0.5px)",
                    borderRadius: "1px",
                  }}
                />
              </Box>
            </Box>
          )}
          </IconButton>
        </Box>
      </Tooltip>

      {/* Approval Panel */}
      <ApprovalPanel
        data-testid="approval-panel"
        open={isApprovalPanelOpen}
        onClose={handleCloseApprovalPanel}
        channelId={channelId}
        threadId={threadId}
        messageId={selectedMessageId || undefined}
        approvals={approvals.map(approval => ({
          ...approval,
          approval_details: {
            ...approval.approval_details,
            approver: {
              ...approval.approval_details.approver,
              avatar_url: approval.approval_details.approver.avatar_url ?? undefined,
            },
            requested_by: {
              ...approval.approval_details.requested_by,
              avatar_url: approval.approval_details.requested_by.avatar_url ?? undefined,
            },
          },
          approval_history: (approval.approval_history ?? []).map(history => ({
            ...history,
            user: {
              ...history.user,
              avatar_url: history.user.avatar_url ?? undefined,
            },
          })),
          context: approval.context ?? {},
        }))}
        onApprovalUpdated={handleApprovalUpdated}
        approvalsLoading={approvalsLoading}
      />
    </>
  );
};

export default ApprovalPanelButton;
